/**
 * Rayur theme — shared runtime utilities.
 * Vanilla JS only, no framework. Each section's own script hooks into
 * these shared helpers instead of re-implementing observers per section.
 */
(() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hasHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  /**
   * Reveals [data-reveal] elements within `root` as they enter the viewport,
   * stamping --reveal-index for staggered CSS transition-delay.
   */
  function initScrollReveal(root = document) {
    const items = Array.from(root.querySelectorAll('[data-reveal]:not(.is-observed)'));
    if (!items.length) return;

    if (prefersReducedMotion.matches) {
      items.forEach((el) => el.classList.add('is-revealed', 'is-observed'));
      return;
    }

    items.forEach((el, i) => {
      if (!el.style.getPropertyValue('--reveal-index')) {
        el.style.setProperty('--reveal-index', el.dataset.revealIndex ?? i);
      }
      el.classList.add('is-observed');
    });

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );

    items.forEach((el) => observer.observe(el));
  }

  /**
   * Custom cursor: a small dot that trails the pointer with slight lag and
   * scales up over interactive elements. Skipped entirely on touch devices
   * and under reduced motion — it's decoration, never a replacement for the
   * native cursor's affordance.
   */
  function initCustomCursor() {
    if (prefersReducedMotion.matches || !hasHover.matches) return;
    if (document.querySelector('.cursor-dot')) return;

    const dot = document.createElement('div');
    dot.className = 'cursor-dot';
    dot.setAttribute('aria-hidden', 'true');
    document.body.appendChild(dot);

    let targetX = window.innerWidth / 2;
    let targetY = window.innerHeight / 2;
    let x = targetX;
    let y = targetY;
    let raf = null;

    const render = () => {
      x += (targetX - x) * 0.2;
      y += (targetY - y) * 0.2;
      dot.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      raf = requestAnimationFrame(render);
    };

    window.addEventListener('pointermove', (e) => {
      targetX = e.clientX;
      targetY = e.clientY;
      dot.classList.add('is-visible');
    });

    document.addEventListener('pointerover', (e) => {
      dot.classList.toggle('is-hovering', !!e.target.closest('a, button, [data-cursor-hover]'));
    });

    document.addEventListener('mouseleave', () => dot.classList.remove('is-visible'));

    raf = requestAnimationFrame(render);
    window.Rayur._cursorRaf = raf;
  }

  /**
   * Magnetic buttons: elements with [data-magnetic] nudge toward the cursor
   * within their own bounds, snapping back on leave. Desktop-only.
   */
  function initMagneticElements(root = document) {
    if (prefersReducedMotion.matches || !hasHover.matches) return;

    root.querySelectorAll('[data-magnetic]:not(.is-magnetic-bound)').forEach((el) => {
      el.classList.add('is-magnetic-bound');
      const strength = parseFloat(el.dataset.magneticStrength) || 0.35;

      el.addEventListener('mousemove', (e) => {
        const rect = el.getBoundingClientRect();
        const relX = e.clientX - rect.left - rect.width / 2;
        const relY = e.clientY - rect.top - rect.height / 2;
        el.style.transform = `translate(${relX * strength}px, ${relY * strength}px)`;
      });

      el.addEventListener('mouseleave', () => {
        el.style.transform = '';
      });
    });
  }

  /**
   * Duplicates marquee content until it fills at least 2x the track width,
   * so the CSS scroll-left keyframe loops seamlessly regardless of how much
   * text a merchant enters in the schema setting.
   */
  function initMarquees(root = document) {
    root.querySelectorAll('[data-marquee]:not(.is-marquee-ready)').forEach((track) => {
      track.classList.add('is-marquee-ready');
      const original = track.innerHTML;
      const minWidth = track.parentElement.offsetWidth * 2;
      while (track.scrollWidth < minWidth && track.children.length < 40) {
        track.insertAdjacentHTML('beforeend', original);
      }
    });
  }

  window.Rayur = window.Rayur || {};
  window.Rayur.prefersReducedMotion = prefersReducedMotion;
  window.Rayur.initScrollReveal = initScrollReveal;
  window.Rayur.initMagneticElements = initMagneticElements;
  window.Rayur.initMarquees = initMarquees;

  document.addEventListener('DOMContentLoaded', () => {
    initScrollReveal(document);
    initCustomCursor();
    initMagneticElements(document);
    initMarquees(document);
  });

  document.addEventListener('shopify:section:load', (e) => {
    initScrollReveal(e.target);
    initMagneticElements(e.target);
    initMarquees(e.target);
  });
})();
